import { GameManager } from "./game-manager";

export interface GameMetadata {
  id: string;
  name: string;
  icon: string;
  version: string;
  author: string;
  description: string;
  modes: ("local" | "online")[];
  maxPlayers: number;
  minPlayers: number;
  path: string;
  rank?: boolean;
  thumbnail?: string;
  tags: string[];
  enabled: boolean;
}

interface GameRegistry {
  games: GameMetadata[];
}

const IDB_CACHE_KEY = "games-registry";

class GameRegistryManager {
  private registry: GameMetadata[] | null = null;
  private loading: Promise<GameMetadata[]> | null = null;

  async loadRegistry(): Promise<GameMetadata[]> {
    if (this.registry) {
      return this.registry;
    }

    if (this.loading) {
      return this.loading;
    }

    const idbCache = await GameManager.loadData(IDB_CACHE_KEY);
    if (idbCache.success && idbCache.data) {
      this.registry = idbCache.data;
      console.log("[GameRegistryManager] Loaded from IndexedDB", this.registry);
    }

    this.loading = this.fetchRegistry();
    this.registry = await this.loading;
    this.loading = null;

    await GameManager.saveData(IDB_CACHE_KEY, this.registry);
    console.log("[GameRegistryManager] Loaded from server", this.registry);

    return this.registry;
  }

  private async fetchRegistry(): Promise<GameMetadata[]> {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_GAME_URL}/games-registry.json`,
      );
      if (!response.ok) {
        throw new Error("Failed to load game registry");
      }
      const data: GameRegistry = await response.json();
      return data.games.filter((game) => game.enabled);
    } catch (error) {
      console.error("Error loading game registry:", error);
      return [];
    }
  }

  getGameById(id: string): GameMetadata | undefined {
    return this.registry?.find((game) => game.id === id);
  }

  getGamesByMode(mode: "local" | "online"): GameMetadata[] {
    return this.registry?.filter((game) => game.modes.includes(mode)) || [];
  }

  getAllGames(): GameMetadata[] {
    return this.registry || [];
  }

  clearCache(): void {
    this.registry = null;
    this.loading = null;
  }
}

export const gameRegistry = new GameRegistryManager();

export const getLocalGames = async (): Promise<GameMetadata[]> => {
  await gameRegistry.loadRegistry();
  return gameRegistry.getGamesByMode("local");
};

export const getOnlineGames = async (): Promise<GameMetadata[]> => {
  await gameRegistry.loadRegistry();
  return gameRegistry.getGamesByMode("online");
};

export const getGameTypeLabel = (gameId: string): string => {
  const game = gameRegistry.getGameById(gameId);
  return game ? `${game.icon} ${game.name}` : gameId;
};

export const getGameMetadata = (gameId: string): GameMetadata | undefined => {
  return gameRegistry.getGameById(gameId);
};
